import React, {useEffect, useMemo, useRef, useState} from "react";
import {Wrapper, Status} from "@googlemaps/react-wrapper";

import {GoogleMapApiKey, GoogleMapLibraries} from "../../../../config";
import AddressAttributeForm from "../../../../components/AttributeForm/AddressAttributeForm";
import RadioGroupForm from "../../../../components/AttributeForm/RadioGroupForm";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import {useAccommodationsPage} from "../../AccommodationContext";
import AttributeList from "../../../../components/AttributeList";
import {useHome} from "../HomeContext";
import {useHomeAction} from "../HomeActionContext";

import PlaceIcon from "@mui/icons-material/Place";
import MapIcon from '@mui/icons-material/Map';

const defaultPosition = {
    latitude: 46.541034,
    longitude: 10.136232,
}

const HomePosition = () => {
    const {addressStreet, addressNumber, position, locationId, updateHome} = useHome();
    const {formAddress, formLocationId, handleSubmit} = useHomeAction();
    const {showSuccessNotification, showErrorNotification, askConfirmation} = useFeedbackTools();
    const {locations, locationMap} = useAccommodationsPage();

    const markerPosition = useMemo(
        () => ({
            lat: position.latitude,
            lng: position.longitude
        }),
        [position.latitude, position.longitude]
    )

    const centerPosition = useMemo(
        () => ({
            lat: position.latitude || defaultPosition.latitude,
            lng: position.longitude || defaultPosition.longitude
        }),
        [position.latitude, position.longitude]  // TODO change the center if the distance from the previous is large enough
    )

    const updateLatLng = (lat, lng) => {
        const innerUpdate = async () => {
            try {
                await updateHome({latitude: lat, longitude: lng});
                showSuccessNotification("Aggiornamento effettuato")
            } catch (e) {
                showErrorNotification(e)
            }
        }

        askConfirmation({
            title: "Aggiorna posizione",
            description: `Confermi di voler modificare la posizione?`,
            confirmationText: 'Si',
            cancellationText: 'No',
            onConfirm: innerUpdate
        });
    }

    const attributes = [
        {
            label: "Indirizzo",
            value: `${addressStreet}, ${addressNumber}`,
            defaultValue: "Non presente",
            icon: <PlaceIcon/>,
            formDialog: formAddress,
            formComponent: <AddressAttributeForm label="Indirizzo"
                                                 streetValue={addressStreet}
                                                 streetAttributeName="addressStreet"
                                                 streetNumberValue={addressNumber}
                                                 streetNumberAttributeName="addressNumber"
                                                 onClose={formAddress.close}
                                                 onSubmit={(data) => handleSubmit(formAddress, data)}
                                                 isLoading={formAddress.loading}/>
        },
        {
            label: "Località",
            value: locationMap[locationId]?.name,
            defaultValue: "Non presente",
            icon: <MapIcon/>,
            formDialog: formLocationId,
            formComponent: <RadioGroupForm attribute="locationId"
                                           label="Località"
                                           value={locationId}
                                           onClose={formLocationId.close}
                                           onSubmit={(data) => handleSubmit(formLocationId, data)}
                                           options={locations.map((l) => ({
                                               label: l.name,
                                               value: l.id,
                                           }))}
                                           isLoading={formLocationId.loading}/>
        }
    ]


    return <>
        <AttributeList attributes={attributes}/>

        <Wrapper apiKey={GoogleMapApiKey} libraries={GoogleMapLibraries} render={(status) => {
            switch (status) {
                case Status.LOADING:
                    return "Caricamento...";
                case Status.SUCCESS:
                    return <Map
                        onMarkerDropEnd={updateLatLng}
                        centerPosition={centerPosition}
                        markerPosition={markerPosition}
                        zoom={position.latitude ? 17 : 14}/>;
                default:
                    return "Errore caricamento servizi mappa";
            }
        }}/>
    </>
}

const Map = (props) => {
    const {centerPosition, zoom, markerPosition, onMarkerDropEnd} = props
    const ref = useRef();
    const [map, setMap] = useState(null);
    const [marker, setMarker] = useState(null)

    useEffect(
        () => {
            const map = new window.google.maps.Map(ref.current, {center: centerPosition, zoom})
            map.setOptions({
                styles: [
                    {
                        featureType: "poi.business",
                        stylers: [{visibility: "off"}],
                    }
                ]
            })
            setMap(map)
        },
        [ref, centerPosition, zoom]
    );

    useEffect(
        () => {
            if (map) {
                if (marker) {
                    marker.setMap(null);
                    window.google.maps.event.clearInstanceListeners(marker);
                }
                if (markerPosition.lat && markerPosition.lng) {
                    const newMarker = new window.google.maps.Marker({
                        position: markerPosition,
                        map: map,
                        draggable: true
                    })
                    newMarker.addListener("dragend", () => {
                        const lat = newMarker.getPosition().lat();
                        const lng = newMarker.getPosition().lng();
                        onMarkerDropEnd(lat, lng);
                    })
                    setMarker(newMarker)
                }
            }
        },
        [map, markerPosition]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    return <div ref={ref} id="map" style={{width: "100%", height: "500px"}}/>;
}

export default HomePosition;